import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AnalysisReportService, TemplateReport } from '../services/analysis-report.service';
import { TemplateService } from '../services/template.service';

type SortColumn = 'templateName' | 'questionCount' | 'confidenceScore';
type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-analysis-report',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './analysis-report.component.html',
  styleUrl: './analysis-report.component.css',
  standalone: true
})
export class AnalysisReportComponent implements OnInit, AfterViewInit, OnDestroy {
  currentTheme: 'light' | 'dark' = 'light';
  isLoading: boolean = false;
  templates: TemplateReport[] = [];
  searchQuery: string = '';
  sortColumn: SortColumn = 'templateName';
  sortDirection: SortDirection = 'asc';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private analysisReportService: AnalysisReportService,
    private templateService: TemplateService
  ) {}

  get cachedTemplateId(): string | null {
    return this.templateService.getCachedTemplateId();
  }

  ngOnInit() {
    this.initializeTheme();
    this.loadTemplates();
  }

  ngAfterViewInit() {
    this.initializeLucideIcons();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadTemplates() {
    this.isLoading = true;
    this.analysisReportService.getTemplates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.templates = data;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Failed to load templates:', err);
          this.isLoading = false;
          this.templates = [];
        }
      });
  }

  // Stats getters
  get totalSurveys(): number {
    return this.templates.length;
  }

  get highConfidenceCount(): number {
    return this.templates.filter(t => t.confidenceScore >= 85).length;
  }

  get mediumConfidenceCount(): number {
    return this.templates.filter(t => t.confidenceScore >= 70 && t.confidenceScore < 85).length;
  }

  get lowConfidenceCount(): number {
    return this.templates.filter(t => t.confidenceScore < 70).length;
  }

  get filteredTemplates(): TemplateReport[] {
    let result = this.templates;
    
    // Filter by search query (case-insensitive)
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase().trim();
      result = result.filter(t => 
        t.templateName.toLowerCase().includes(query) ||
        t.templateId.toString().includes(query)
      );
    }
    
    // Sort
    result = [...result].sort((a, b) => {
      let comparison = 0;
      if (this.sortColumn === 'templateName') {
        comparison = a.templateName.localeCompare(b.templateName);
      } else if (this.sortColumn === 'questionCount') {
        comparison = a.questionCount - b.questionCount;
      } else if (this.sortColumn === 'confidenceScore') {
        comparison = a.confidenceScore - b.confidenceScore;
      }
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return result;
  }

  toggleSort(column: SortColumn) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  formatScore(score: number): string {
    return score.toFixed(1);
  }

  getScoreColor(score: number): string {
    if (score >= 85) return '#10b981';
    if (score >= 70) return '#f59e0b';
    return '#ef4444';
  }

  getDonutStyle(score: number): string {
    const color = this.getScoreColor(score);
    const percentage = Math.min(score, 100);
    return `conic-gradient(${color} ${percentage * 3.6}deg, #e5e7eb ${percentage * 3.6}deg)`;
  }

  getDonutStyleDark(score: number): string {
    const color = this.getScoreColor(score);
    const percentage = Math.min(score, 100);
    return `conic-gradient(${color} ${percentage * 3.6}deg, #374151 ${percentage * 3.6}deg)`;
  }

  getScoreIcon(score: number): string {
    if (score >= 85) return 'trending-up';
    if (score >= 70) return 'minus';
    return 'trending-down';
  }

  getConfidenceLabel(score: number): string {
    if (score >= 85) return 'High';
    if (score >= 70) return 'Medium';
    return 'Low';
  }

  formatLastRunDate(dateString: string | null): string {
    if (!dateString) return 'Not run yet';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  viewQuestions(template: TemplateReport) {
    this.router.navigate(['/analysis-report-questions'], {
      state: { 
        templateId: template.templateId,
        templateName: template.templateName
      }
    });
  }

  goToDocumentation() {
    this.router.navigate(['/documentation']);
  }

  initializeTheme() {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark';
    if (savedTheme) {
      this.currentTheme = savedTheme;
      this.applyTheme(savedTheme);
    }
  }

  setTheme(theme: 'light' | 'dark') {
    this.currentTheme = theme;
    this.applyTheme(theme);
    localStorage.setItem('theme', theme);
  }

  private applyTheme(theme: 'light' | 'dark') {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  private initializeLucideIcons() {
    if (typeof (window as any).lucide !== 'undefined') {
      (window as any).lucide.createIcons({
        attrs: {
          'stroke-width': 1.5
        }
      });
    }
  }
}
